import { useRef, useState } from 'react';

import { NavLink } from 'react-router-dom';

import {
  Avatar,
  Box,
  Button,
  Divider,
  Hidden,
  lighten,
  List,
  ListItem,
  ListItemText,
  Popover,
  Typography
} from '@mui/material';

import LockOpenTwoToneIcon from '@mui/icons-material/LockOpenTwoTone';
import AccountTreeTwoToneIcon from '@mui/icons-material/AccountTreeTwoTone';
import KeyboardArrowRightTwoToneIcon from '@mui/icons-material/KeyboardArrowRightTwoTone';
import TranslateTwoToneIcon from '@mui/icons-material/TranslateTwoTone';
import NotificationsTwoToneIcon from '@mui/icons-material/NotificationsTwoTone';

import HeaderNotifications from '../Buttons/Notifications';
import LanguageSwitcher from 'src/components/LanguageSwitcher';

import { styled } from '@mui/material/styles';

const UserBoxButton = styled(Button)(
  ({ theme }) => `
        padding-left: ${theme.spacing(1)};
        padding-right: ${theme.spacing(1)};
        width: 100%;
        display: flex;
        justify-content: space-between;
`
);

const MenuUserBox = styled(Box)(
  ({ theme }) => `
        background: ${theme.colors.alpha.black[5]};
        padding: ${theme.spacing(2)};
`
);

const UserBoxText = styled(Box)(
  ({ theme }) => `
        text-align: left;
        padding-left: ${theme.spacing(0.5)};
`
);

const UserBoxLabel = styled(Typography)(
  ({ theme }) => `
        font-weight: ${theme.typography.fontWeightBold};
        color: ${theme.palette.secondary.main};
        display: block;
`
);

const UserBoxDescription = styled(Typography)(
  ({ theme }) => `
        color: ${lighten(theme.palette.secondary.main, 0.5)}
`
);

import { useTranslation } from 'react-i18next';
import useAuthStore from 'src/store/useAuthStore';
import { resolveMediaUrl } from 'src/services/api';

function HeaderUserbox() {
  const { t } = useTranslation();
  const { user, logout } = useAuthStore();

  const ref = useRef<any>(null);
  const [isOpen, setOpen] = useState<boolean>(false);

  const handleOpen = (): void => {
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  const handleLogout = (): void => {
    handleClose();
    logout();
  };

  const displayName = user?.full_name || user?.name || user?.email || t('common.user', 'User');
  const roleMap: Record<string, string> = {
    super_admin: t('user_report.role_super_admin', 'Super Admin'),
    tenant_admin: t('user_report.role_tenant_admin', 'Tenant Admin'),
    unit_manager: t('user_report.role_unit_manager', 'Unit Manager'),
    end_user: t('user_report.role_end_user', 'User')
  };
  const displayRole = (user?.role && roleMap[user.role]) || t('common.user', 'User');
  const displayAvatar = resolveMediaUrl(user?.avatar_url);

  return (
    <>
      <UserBoxButton color="secondary" ref={ref} onClick={handleOpen}>
        <Box display="flex" alignItems="center">
          <Avatar variant="rounded" alt={displayName} src={displayAvatar} />
          <Hidden mdDown>
            <UserBoxText>
              <UserBoxLabel variant="body1">{displayName}</UserBoxLabel>
              <UserBoxDescription variant="body2">
                {displayRole}
              </UserBoxDescription>
            </UserBoxText>
          </Hidden>
        </Box>
        <Hidden smDown>
          <KeyboardArrowRightTwoToneIcon sx={{ ml: 1 }} />
        </Hidden>
      </UserBoxButton>
      <Popover
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
      >
        <MenuUserBox sx={{ minWidth: 210 }} display="flex">
          <Avatar variant="rounded" alt={displayName} src={displayAvatar} />
          <UserBoxText>
            <UserBoxLabel variant="body1">{displayName}</UserBoxLabel>
            <UserBoxDescription variant="body2">
              {displayRole}
            </UserBoxDescription>
          </UserBoxText>
        </MenuUserBox>
        <Divider sx={{ mb: 0 }} />
        <List sx={{ p: 1 }} component="nav">
          <ListItem sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box display="flex" alignItems="center">
              <TranslateTwoToneIcon fontSize="small" sx={{ mr: 2, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.primary">
                {t('header.language', 'Language')}
              </Typography>
            </Box>
            <LanguageSwitcher />
          </ListItem>
          <ListItem sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box display="flex" alignItems="center">
              <NotificationsTwoToneIcon fontSize="small" sx={{ mr: 2, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.primary">
                {t('common.notifications', 'Notifications')}
              </Typography>
            </Box>
            <HeaderNotifications />
          </ListItem>
          <ListItem
            button
            to="/management/profile/settings"
            component={NavLink}
            onClick={handleClose}
          >
            <AccountTreeTwoToneIcon fontSize="small" />
            <ListItemText primary={t('header.account_settings')} />
          </ListItem>
        </List>
        <Divider />
        <Box sx={{ m: 1 }}>
          <Button color="primary" fullWidth onClick={handleLogout}>
            <LockOpenTwoToneIcon sx={{ mr: 1 }} />
            {t('header.sign_out')}
          </Button>
        </Box>
      </Popover>
    </>
  );
}

export default HeaderUserbox;
